import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOauthSession } from "@/lib/api";
import {
  consumeFreshOauthFlow,
  consumeStoredReturnTo,
  resolveAuthRedirect,
} from "@/lib/auth-redirect";
import { logger } from "@/lib/logger";

interface ParsedCallback {
  accessToken: string | null;
  error: string | null;
  errorDescription: string | null;
}

/**
 * Supabase returns tokens in the URL FRAGMENT (`#access_token=...`) on success
 * and errors as either fragment or query params. Read both, fragment first.
 */
function parseCallback(): ParsedCallback {
  const rawHash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hash = new URLSearchParams(rawHash);
  const query = new URLSearchParams(window.location.search);
  const get = (key: string) => hash.get(key) ?? query.get(key);
  return {
    accessToken: get("access_token"),
    error: get("error"),
    errorDescription: get("error_description"),
  };
}

/**
 * OAuth landing page. On mount it exchanges the provider's access token for our
 * HTTP-only session cookie, then redirects onward. The access token is stripped
 * from the URL bar before the exchange, and is never stored anywhere.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { mutate: startSession } = useOauthSession();
  const ranRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  // Run-once on mount: this is a side effect (parse URL, fire a mutation), not
  // data loading. TanStack Query owns the exchange; useEffect only triggers it.
  useEffect(() => {
    if (ranRef.current) {
      return;
    }
    ranRef.current = true;

    // Consume the one-shot flow marker exactly once, up front, so it's cleared
    // on every outcome below. The ref guard above makes this idempotent under
    // StrictMode's double-invoke: the second run bails before reaching here.
    const flowIsOurs = consumeFreshOauthFlow();

    const parsed = parseCallback();
    // Scrub the token/error from the URL bar, history, and referrer as soon as
    // it's parsed (before ANY early return) so a rejected path (invalid
    // marker, provider error, no token) can't leave the token in the fragment.
    if (parsed.accessToken || parsed.error) {
      window.history.replaceState(null, "", window.location.pathname);
    }

    if (parsed.error) {
      logger.debug("[auth] OAuth callback error:", parsed.error, parsed.errorDescription);
      // Clear any stashed return-to so this failed/cancelled attempt can't
      // redirect a later, unrelated sign-in.
      consumeStoredReturnTo();
      setError(parsed.errorDescription || "Sign-in was cancelled or failed.");
      return;
    }
    if (!parsed.accessToken) {
      consumeStoredReturnTo();
      setError("No sign-in token was returned. Please try again.");
      return;
    }

    // Login-CSRF guard: only exchange a token for a flow THIS browser started
    // and that is still fresh. A phished callback URL carrying a foreign token
    // has no valid marker, so we refuse to exchange it.
    if (!flowIsOurs) {
      logger.debug("[auth] OAuth callback without a valid in-flight marker → refusing exchange");
      consumeStoredReturnTo();
      setError(
        "This sign-in link is invalid or has expired. Please start again from the sign-in page.",
      );
      return;
    }

    startSession(
      { accessToken: parsed.accessToken },
      {
        onSuccess: (data) => {
          const returnTo = consumeStoredReturnTo();
          const destination = resolveAuthRedirect({ role: data?.role, returnTo });
          logger.debug("[auth] OAuth session established → redirecting to", destination);
          navigate(destination, { replace: true });
        },
        onError: (err) => {
          logger.debug("[auth] OAuth session exchange failed:", err);
          consumeStoredReturnTo();
          setError("We couldn't complete your sign-in. Please try again.");
        },
      },
    );
  }, [startSession, navigate]);

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4">
      {error ? (
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-primary text-2xl font-normal tracking-tight">
              Sign-in failed
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted mb-6 text-sm">{error}</p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-accent h-6 w-6 animate-spin" />
          <p className="text-muted text-sm">Signing you in…</p>
        </div>
      )}
    </div>
  );
}
